import React, { useState, useEffect, useRef } from "react";
import { Menu, X, ChevronLeft, LogOut, User } from "lucide-react";
import Logo from "src/components/molecules/logo/Logo";
import SidebarItem from "src/components/molecules/sidebar-item/SidebarItem";
import { MenuItems } from "src/types/models/menu";
import { useAuth } from "../auth/auth-context";
import { ReactComponent as BrandingIcon } from "../../../assets/branding.svg";
import { ReactComponent as RegistrationIcon } from "../../../assets/registration.svg";
import { ReactComponent as BillingIcon } from "../../../assets/billing.svg";
import { ReactComponent as HelpIcon } from "../../../assets/help.svg";
import { ReactComponent as SwitchIcon } from "../../../assets/svg/switchIcon.svg";
import { ReactComponent as AccountIcon } from "../../../assets/svg/accountIcon.svg";
import { ReactComponent as AdministrationIcon } from "../../../assets/svg/administrationIcon.svg";
import { BusinessViewModel } from "../business-list/business-list.view-model";
import { useNavigate } from "react-router-dom";
import { AccountViewModel } from "../account-selection/account-selection-view-model";
import { TProductPlan } from "src/components/molecules/product-plan-marker/product-plan-marker";

interface SidebarProps {
	OnToggleDrawer: (open: boolean) => void;
	OnItemClick: (name: string) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ OnToggleDrawer, OnItemClick }) => {
	type Section = "registration" | "branding" | "billing" | "help";

	const footerRef = useRef(null); // Ref for the footer
	const { user, logout } = useAuth();
	const [accountName, setAccountName] = useState("");
	const [product, setProduct] = useState<TProductPlan>("Demo");
	const navigate = useNavigate();
	const [drawerOpen, setDrawerOpen] = useState(true);
	const [showMenu, setShowMenu] = useState(false);
	const [hasApprovedBusiness, setHasApprovedBusiness] = useState(false);
	const [canSwitchAccount, setCanSwitchAccount] = useState(false);
	const [canApproveBusiness, setCanApproveBusiness] = useState(false);
	const [canListBrandingApplications, setCanListBrandingApplications] =
		useState(false);
	const [canCreateReseller, setCanCreateReseller] = useState(false);
	const [openSections, setOpenSections] = useState({
		registration: true,
		branding: true,
		billing: true,
		administration: true,
		help: true,
	});

	const viewModel = useRef<BusinessViewModel | null>(null);
	const accountViewModel = useRef<AccountViewModel | null>(null);

	// State for selected item
	const [selectedItem, setSelectedItem] = useState<string>("");

	useEffect(() => {
		const accountHasApprovedBusiness = async () => {
			if (user && user.accessToken) {
				accountViewModel.current = new AccountViewModel(
					user.accessToken
				);

				setCanApproveBusiness(
					user.permissionBag.permissions.filter(
						(p) => p.name === "Business" && p.value === "Approve"
					).length > 0
				);

				setCanCreateReseller(
					user.permissionBag.permissions.filter(
						(p) =>
							p.name === "ResellerContact" && p.value === "Create"
					).length > 0
				);

				setCanListBrandingApplications(
					user.permissionBag.permissions.filter(
						(p) => p.name === "Application" && p.value === "List"
					).length > 0
				);

				viewModel.current = new BusinessViewModel(user.accessToken);
				await fetchAccountsAsync();
				const businesses = await viewModel.current?.fetchListAsync();

				if (businesses) {
					if (
						businesses.filter(
							(p) =>
								p.status === "Active" || p.status === "Approved"
						).length > 0
					) {
						setHasApprovedBusiness(true);
					} else {
						setHasApprovedBusiness(false);
					}
				}
			}
		};
		accountHasApprovedBusiness();
	}, []);

	useEffect(() => {
		OnToggleDrawer(drawerOpen);
	}, [drawerOpen]);

	const fetchAccountsAsync = async () => {
		if (accountViewModel.current) {
			const currentAccount =
				await accountViewModel.current.getAccountAsync();

			if (currentAccount) {
				setAccountName(currentAccount.name);
				setProduct(currentAccount.productPlan);
			}

			if (user) {
				const hasAccountList =
					user.permissionBag.permissions.filter(
						(p) => p.name === "Account" && p.value === "List"
					).length > 0;
				setCanSwitchAccount(
					user.has_multiple_accounts ||
						user.reseller_id != "" ||
						hasAccountList
				);
			}
		}
	};

	const toggleDrawer = () => setDrawerOpen((prev) => !prev);

	const toggleMenu = () => setShowMenu(!showMenu);

	const handleSectionClick = (sectionName: string) => {
		setOpenSections((prev) => ({
			...prev,
			[sectionName]: !prev[sectionName as Section],
		}));

		setSelectedItem(sectionName); // Update selected item
		OnItemClick(sectionName);
	};

	// Handle item selection
	const handleItemClick = (itemName: string) => {
		setSelectedItem(itemName); // Update selected item
		OnItemClick(itemName);
	};

	const handleProfile = () => {
		handleItemClick("userProfile");
		setShowMenu(false);
	};

	const handleTeam = () => {
		handleItemClick("userManagement");
		setShowMenu(false);
	};

	const handleLogout = () => logout();

	const handleAccountManagement = () => {
		handleItemClick("accountManagement");
		setShowMenu(false);
	};

	const handleSwitchAccount = () => {
		navigate("/select-account");
	};

	const handleClickOutside = (event: MouseEvent) => {
		if (
			footerRef.current &&
			!(footerRef.current as HTMLElement).contains(event.target as Node)
		) {
			setShowMenu(false);
		}
	};

	useEffect(() => {
		// Attach the event listener when the menu is open
		if (showMenu) {
			document.addEventListener("mousedown", handleClickOutside);
		} else {
			document.removeEventListener("mousedown", handleClickOutside);
		}

		// Cleanup the event listener
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [showMenu]);

	return (
		<div className="flex h-screen">
			{/* Mini Drawer */}
			<div
				className={`${
					drawerOpen ? "hidden" : "flex"
				} w-14 h-full bg-gray-200 flex items-center justify-center`}
			>
				<button onClick={toggleDrawer}>
					<Menu className="w-6 h-6 text-gray-600" />
				</button>
			</div>

			{/* Full Drawer */}
			<div
				className={`${
					drawerOpen ? "w-80" : "w-0 hidden"
				} flex flex-col bg-white h-full transition-all duration-300`}
			>
				{/* Header */}
				<div className="flex flex-col items-center justify-between gap-4 px-4 py-4 border-b border-gray-300 w-full">
					<div
						onClick={() => handleSectionClick("dashboard")}
						className="flex flex-col items-center gap-1"
					>
						<Logo />
						<span
							onClick={() => handleSectionClick("dashboard11")}
							className="font-Poppins font-semibold text-[10px] tracking-widest text-gray-600"
						>
							By Expertstack
						</span>
					</div>
					<button onClick={toggleDrawer}>
						<ChevronLeft className="w-6 h-6 text-gray-600" />
					</button>
					<div className="w-full flex flex-col items-center justify-center gap-2">
						<div className="bg-sky-300 p-2 drop-shadow-lg w-full flex flex-col items-center justify-center">
							<span className="font-Poppins font-bold tracking-wider text-center text-md text-white uppercase">
								{accountName}
							</span>
						</div>
						<div className="p-2 drop-shadow-lg w-full flex flex-col items-center justify-center">
							<span className="font-Poppins font-bold tracking-wider text-center text-md text-gray-900 uppercase">
								{product}
							</span>
						</div>
					</div>
				</div>

				{/* Navigation */}
				<div className="flex-grow overflow-y-auto">
					<SidebarItem
						name="registration"
						display="Registration"
						open={openSections.registration}
						OnSectionClick={handleSectionClick}
						OnItemClick={handleItemClick}
						selectedItem={selectedItem} // Pass selected item
						childs={[
							{
								name: MenuItems.RegistrationBusiness,
								display: "Businesses",
								disabled: product === "Demo",
							},
							{
								name: MenuItems.RegistrationNumbers,
								display: "Numbers",
								disabled:
									product === "Demo" || !hasApprovedBusiness,
							},
							{
								name: MenuItems.RegistrationMobileApps,
								display: "Mobile Apps",
								disabled:
									product === "Demo" || !hasApprovedBusiness,
							},
							{
								name: MenuItems.RegistrationContactCentres,
								display: "Contact Centres",
								disabled:
									product === "Demo" || !hasApprovedBusiness,
							},
							{
								name: MenuItems.RegistrationContactCentres,
								display: "Branding Logos",
								disabled: true,
							},
						]}
						Icon={RegistrationIcon}
					/>

					<SidebarItem
						name="branding"
						display="Branding"
						open={openSections.branding}
						OnSectionClick={handleSectionClick}
						OnItemClick={handleItemClick}
						selectedItem={selectedItem} // Pass selected item
						childs={[
							{
								name: MenuItems.BrandingApplication,
								display: "Applications",
								disabled: !canListBrandingApplications,
							},
							// {
							// 	name: MenuItems.Campaigns,
							// 	display: "Campaigns",
							// 	disabled:
							// 		product === "Demo" || !hasApprovedBusiness,
							// },
							// {
							// 	name: MenuItems.BrandingApplication,
							// 	display: "Brands",
							// },
							// {
							// 	name: MenuItems.BrandingApplication,
							// 	display: "Numbers",
							// },
						]}
						Icon={BrandingIcon}
					/>

					<SidebarItem
						name="billing"
						display="Billing"
						open={openSections.billing}
						OnSectionClick={handleSectionClick}
						OnItemClick={handleItemClick}
						selectedItem={selectedItem} // Pass selected item
						childs={[
							{
								name: MenuItems.BrandingApplication,
								display: "Applications",
								disabled: product === "Demo",
							},
						]}
						Icon={BillingIcon}
						disabled
					/>

					{(canApproveBusiness || canCreateReseller) && (
						<SidebarItem
							name="administration"
							display="Administration"
							open={openSections.administration}
							OnSectionClick={handleSectionClick}
							OnItemClick={handleItemClick}
							selectedItem={selectedItem} // Pass selected item
							childs={[
								{
									name: MenuItems.Businesses,
									display: "Businesses",
									disabled: !canApproveBusiness,
								},
								{
									name: MenuItems.Reseller,
									display: "Reseller",
									disabled: !canCreateReseller,
								},
							]}
							Icon={AdministrationIcon}
						/>
					)}

					<SidebarItem
						name="help"
						display="Help"
						open={openSections.help}
						OnSectionClick={handleSectionClick}
						OnItemClick={handleItemClick}
						selectedItem={selectedItem} // Pass selected item
						childs={[
							{
								name: MenuItems.HelpSupport,
								display: "Support",
								disabled: true,
							},
							{
								name: MenuItems.HelpDocumentation,
								display: "Documentation",
								disabled: true,
							},
						]}
						Icon={HelpIcon}
					/>
				</div>

				{/* Footer */}
				<div
					ref={footerRef}
					className="relative bg-gradient-to-r from-sky-900 to-sky-700 p-6 text-center text-white border-t-8 border-orange-600 w-full"
				>
					<div className="flex items-center justify-between w-full">
						<div className="flex items-center justify-center w-full">
							<span className="font-semibold text-md uppercase text-center text-nowrap overflow-hidden whitespace-nowrap">
								{user?.givenName} {user?.familyName}
							</span>
						</div>
						<button onClick={toggleMenu}>
							{showMenu ? <X className="w-6 h-6" /> : <Menu />}
						</button>
					</div>
					{showMenu && (
						<div className="absolute -left-0 bottom-20 w-full bg-white text-gray-800 shadow-lg">
							<ul>
								<li
									className="flex items-center p-3 cursor-pointer bg-neutral-100 hover:bg-sky-100 font-Poppins font-semibold text-sm border-b-2 border-orange-200"
									onClick={handleProfile}
								>
									<User className="w-5 h-5 mr-3" />
									Profile
								</li>
								{/* {user?.permissionBag.permissions.some(
									(p) =>
										p.name === "Contact" &&
										p.value === "Create"
								) && (
									<li
										className="flex items-center p-3 cursor-pointer bg-neutral-100 hover:bg-sky-100 font-Poppins font-semibold text-sm border-b-2 border-orange-200"
										onClick={handleTeam}
									>
										<UsersIcon className="w-5 h-5 mr-3" />
										Team
									</li>
								)} */}
								{user?.accountId && (
									<li
										className="flex items-center p-3 cursor-pointer bg-neutral-100 hover:bg-sky-100 font-Poppins font-semibold text-sm border-b-2 border-orange-200"
										onClick={handleAccountManagement}
									>
										<AccountIcon className="w-5 h-5 mr-3" />
										Account
									</li>
								)}
								{canSwitchAccount && (
									<li
										className="flex items-center p-3 cursor-pointer bg-neutral-100 hover:bg-sky-100 font-Poppins font-semibold text-sm border-b-2 border-orange-200"
										onClick={handleSwitchAccount}
									>
										<SwitchIcon className="w-5 h-5 mr-3" />
										Switch account {canSwitchAccount}
									</li>
								)}
								<li
									className="flex items-center p-3 cursor-pointer bg-neutral-100 hover:bg-sky-100 font-Poppins font-semibold text-sm"
									onClick={handleLogout}
								>
									<LogOut className="w-5 h-5 mr-3" />
									Logout
								</li>
							</ul>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default Sidebar;
